import { FormGroup } from 'reactstrap';
import { CheckboxInput, CheckboxProps, updateFn } from '@g17eco/molecules/form';
import { Toggle } from '../../../molecules/toggle';
import { TemplateConfigFormData } from '@features/survey-templates/types';
import { ConfigFormData } from '../types';
import { QUESTION, SURVEY } from '@constants/terminology';
import { CollapseGroup } from '@g17eco/molecules/collapse-panel';

interface SurveyQuestionFormProps {
  disabled: boolean;
  isCheckboxDisabled: boolean;
  checkOptions: Pick<CheckboxProps, 'options' | 'groupCode'>;
  form:
    | ConfigFormData
    | TemplateConfigFormData
    | Pick<
        ConfigFormData,
        'verificationRequired' | 'evidenceRequired' | 'noteRequired' | 'isPrivate' | 'bulkVerifyEnabled'
      >;
  updateForm: updateFn;
}

// Component to render checkboxes with optional toggle
const CheckboxesWithToggle = (
  props: Pick<SurveyQuestionFormProps, 'checkOptions' | 'form' | 'updateForm' | 'isCheckboxDisabled'>,
) => {
  const { checkOptions, form, updateForm, isCheckboxDisabled } = props;

  const verificationOption = checkOptions.options.find((option) => option.code === 'verificationRequired');

  if (!verificationOption) {
    return (
      <CheckboxInput
        disabled={isCheckboxDisabled}
        groupCode={checkOptions.groupCode}
        options={checkOptions.options}
        form={form}
        onChange={updateForm}
      />
    );
  }

  // Get all options except the verification option
  const otherOptions = checkOptions.options.filter((option) => option.code !== 'verificationRequired');

  return (
    <>
      {/* Verification checkbox */}
      <CheckboxInput
        disabled={isCheckboxDisabled}
        groupCode={checkOptions.groupCode}
        options={[verificationOption]}
        form={form}
        onChange={updateForm}
      />

      {/* Bulk verification toggle after verification checkbox */}
      <Toggle
        checked={Boolean(form.bulkVerifyEnabled)}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          updateForm({ code: 'bulkVerifyEnabled', value: e.target.checked })
        }
        label={`Allow verifiers to verify multiple ${QUESTION.PLURAL} at once on the ${SURVEY.ADJECTIVE} overview page`}
        disabled={isCheckboxDisabled}
        className={{
          form: 'd-flex align-items-center pl-3 mb-2',
          label: `ml-1 ${isCheckboxDisabled ? 'text-ThemeTextPlaceholder' : 'text-ThemeTextLight'}`,
        }}
        style={isCheckboxDisabled ? { opacity: 0.5 } : undefined}
      />

      {/* Other checkboxes */}
      {otherOptions.length > 0 && (
        <CheckboxInput
          disabled={isCheckboxDisabled}
          groupCode={checkOptions.groupCode}
          options={otherOptions}
          form={form}
          onChange={updateForm}
        />
      )}
    </>
  );
};

export const SurveyQuestionForm = (props: SurveyQuestionFormProps) => {
  const { disabled, isCheckboxDisabled, checkOptions, form, updateForm } = props;

  return (
    <FormGroup className='mt-4' disabled={disabled}>
      <h5>Default {QUESTION.SINGULAR} requirements</h5>
      <div className='ml-2'>
        <CheckboxesWithToggle
          checkOptions={checkOptions}
          form={form}
          updateForm={updateForm}
          isCheckboxDisabled={isCheckboxDisabled}
        />
      </div>
    </FormGroup>
  );
};

export const SurveyQuestionCollapseForm = (props: SurveyQuestionFormProps) => {
  const { disabled, isCheckboxDisabled, checkOptions, form, updateForm } = props;

  return (
    <CollapseGroup initialValue={false} trigger={<h5>{QUESTION.CAPITALIZED_SINGULAR} submission requirements</h5>}>
      <FormGroup disabled={disabled}>
        <div className='ml-2'>
          <CheckboxesWithToggle
            checkOptions={checkOptions}
            form={form}
            updateForm={updateForm}
            isCheckboxDisabled={isCheckboxDisabled}
          />
        </div>
      </FormGroup>
    </CollapseGroup>
  );
};
